// Generic imports
import _ from 'lodash';
import { v4 as uuidv4 } from 'uuid';

// Local imports
import PRODUCT_TYPES, { allProductTypes } from '../../ProductTypes.js';
import { dangerousText } from '../../utils/Dangerous.js';
import { calcVolumeWeight, calcWeight, calcChargeableWeight, calcWeightOrMeasurment, calcTotalVolume } from '../../CalcWeight.js';
import { formulaFromLCLWM } from '../../utils/Formula.js';

/**
 * Normalize sea W/M product types to 'LCL'.
 * Handles legacy types and the new unified type.
 */
export function normalizeProductType(type) {
    if (type === 'Air Freight') return 'Air freight';
    if (type === 'By Dimensions or Weight (sea)') return 'LCL';
    if (type === 'By Volume or Weight (sea)') return 'LCL';
    if (type === 'By Dimensions or Weight (land)') return 'By Volume or Weight (land)';
    return type;
}

// The equipment line for one product, e.g. "2 x 40' HC Dry (Commodity: Toys | Total gross weight:
// 24,000 kg)". The commodity always shows ("General commodity" when none was given); every other part only
// when it has a value: a weight of 0, a missing dimension or an unknown temperature is left out
// rather than printed as "0 kg".
function getProductDescription({ product, transportationMethod, allProducts, organization }) {

    if (!product.type) return '';

    const has = v => v !== undefined && v !== null && v !== '' && !(typeof v === 'number' && isNaN(v));
    const positive = v => has(v) && Number(v) > 0;
    const line = (head, parts) => {
        const shown = parts.filter(Boolean);
        return shown.length ? `${head} (${shown.join(' | ')})` : head;
    };
    // Commodity always shows: "General commodity" when none was given (or it was given as General),
    // otherwise "Commodity: Toys".
    const commodity = c => (!c || String(c).trim().toLowerCase() === 'general' ? 'General commodity' : `Commodity: ${c}`);
    const amount = (label, val, postfix) => (positive(val) ? `${label}: ${Number(val).toLocaleString()} ${postfix}` : null);
    const dimensions = (l, w, h, units) => (positive(l) && positive(w) && positive(h) ? `Dimensions: ${l}${units} x ${w}${units} x ${h}${units}` : null);
    const height = (h, units) => (positive(h) ? `Height: ${h}${units}` : null);
    // 0° is a real temperature, so only a missing one is left out.
    const temperature = (t, units) => (has(t) ? `Temperature: ${t}${units || ''}` : null);
    const dangerous = product.dangerous ? dangerousText(product) : null;
    const grossWeight = () => amount('Total gross weight', (product.quantity || 1) * (product.weight || 0), product.kgOrLbs || 'kg');

    if (product.type === 'Air freight') {
        const units = product.cmOrInches || 'cm';
        const wUnits = product.kgOrLbs || 'kg';
        const round = !!organization?.airRoundChargeableWeight;
        const lines = (product.calculations || []).map(calc => {
            const qty = calc.quantity || 1;
            const synthetic = {
                type: 'Air Freight',
                kgOrLbs: wUnits,
                cmOrInches: units,
                quantity: qty,
                ...(calc.method === 'dimensions' ? { length: calc.length, width: calc.width, height: calc.height } : {}),
                ...(calc.method === 'volume' ? { volume: calc.volume } : {}),
                ...(calc.method === 'chargeable-weight' ? { chargeableWeight: calc.chargeableWeight } : {}),
                weight: calc.weight,
            };
            const parts = [
                calc.method === 'dimensions' ? dimensions(calc.length, calc.width, calc.height, units) : null,
                commodity(calc.commodity || product.commodity),
                amount('Total gross weight', (calc.weight || 0) * qty, wUnits),
            ];
            if (calc.method === 'dimensions' || calc.method === 'volume') {
                const vw = calcVolumeWeight({ product: synthetic, transportationMethod: 'air', round });
                const cw = calcChargeableWeight({ product: synthetic, transportationMethod: 'air', allProducts: [synthetic], round });
                parts.push(amount('Volume weight', vw?.val, vw?.postfix), !round ? amount('Total chargeable weight', cw?.val, cw?.postfix) : null);
            } else {
                parts.push(amount('Total chargeable weight', (calc.chargeableWeight || 0) * qty, wUnits));
            }
            return line(`${qty} x Air Freight`, parts);
        });
        return lines.join('\n') + (product.dangerous ? `\n${dangerous}` : '');
    }

    if (transportationMethod === 'air') {
        const round = organization?.airRoundChargeableWeight;
        const volumeWeight = calcVolumeWeight({ product, transportationMethod: 'air', round });
        const weight = calcWeight({ product });
        const cw = calcChargeableWeight({ product, transportationMethod: 'air', allProducts, round });
        const head = `${product.quantity} x ${product.type}`;

        if (product.width && product.height && product.length) {
            return line(head, [dimensions(product.length, product.width, product.height, product.cmOrInches), commodity(product.commodity), amount('Total gross weight', weight?.val, weight?.postfix), amount('Volume weight', volumeWeight?.val, volumeWeight?.postfix), !round ? amount('Total chargeable weight', cw?.val, cw?.postfix) : null, dangerous]);
        } else if (product.volume) {
            if (product.type.includes('Reefer')) {
                return line(head, [temperature(product.temperature, product.celsiusOrFahrenheit), grossWeight(), commodity(product.commodity), dangerous]);
            }
            return line(head, [commodity(product.commodity), amount('Total gross weight', weight?.val, weight?.postfix), amount('Volume weight', volumeWeight?.val, volumeWeight?.postfix), !round ? amount('Total chargeable weight', cw?.val, cw?.postfix) : null, dangerous]);
        }
        return line(head, [commodity(product.commodity), grossWeight(), !round ? amount('Total chargeable weight', cw?.val, cw?.postfix) : null, dangerous]);
    }

    // A shipment of loose packages (LCL, or land groupage priced the same way): one line per package
    // group, its size and weight per unit in the head, its totals in brackets.
    const packageLines = typeLabel => {
        const units = product.cmOrInches || 'cm';
        const wUnits = product.kgOrLbs || 'kg';
        const divisor = units === 'cm' ? 1000000 : 1728;
        const volPostfix = units === 'cm' ? 'cbm' : 'cft';
        const lines = (product.calculations || []).map(calc => {
            const qty = calc.quantity || 1;
            const perUnitWeight = wUnits === 'kg' ? (calc.weight || 0) / 1000 : (calc.weight || 0) * 0.000453592;
            const perUnitVol = calc.method === 'dimensions' ? (calc.length || 0) * (calc.width || 0) * (calc.height || 0) / divisor : (calc.volume || 0);
            const size = calc.method === 'dimensions'
                ? (positive(calc.length) && positive(calc.width) && positive(calc.height) ? `${calc.length}${units} x ${calc.width}${units} x ${calc.height}${units}` : null)
                : (positive(perUnitVol) ? `${perUnitVol.toLocaleString()} ${volPostfix}` : null);
            const each = [size, positive(perUnitWeight) ? `${perUnitWeight.toLocaleString()} ton` : null].filter(Boolean).join(', ');
            const label = calc.method === 'dimensions' ? `By Dimensions or Weight${typeLabel}` : `By Volume or Weight${typeLabel}`;
            return line(`${qty} x ${label}${each ? ` of ${each}` : ''}`, [
                amount('Total gross weight', perUnitWeight * qty, 'ton'),
                amount('Total volume', perUnitVol * qty, volPostfix),
                commodity(calc.commodity),
            ]);
        });
        return lines.join('\n') + (product.dangerous ? '\n(Dangerous)' : '');
    };

    // Groupage priced by its own size and weight: per unit in the head, totals in brackets.
    const groupageLine = ({ withDimensions }) => {
        const weight = calcWeight({ product });
        if (transportationMethod === 'sea' && weight.postfix === 'kg') {
            weight.postfix = 'ton';
            weight.val = weight.val / 1000;
        }
        const volume = calcTotalVolume({ product });
        const qty = product.quantity || 1;
        const size = withDimensions
            ? (positive(product.length) && positive(product.width) && positive(product.height) ? `${product.length}${product.cmOrInches} x ${product.width}${product.cmOrInches} x ${product.height}${product.cmOrInches}` : null)
            : (positive(volume.val) ? `${(volume.val / qty).toLocaleString()} ${volume.postfix}` : null);
        const each = [size, positive(weight.val) ? `${(weight.val / qty).toLocaleString()} ${weight.postfix}` : null].filter(Boolean).join(', ');
        return line(`${product.quantity} x ${product.type}${each ? ` of ${each}` : ''}`, [
            amount('Total gross weight', weight.val, weight.postfix),
            amount('Total volume', volume.val, volume.postfix),
            commodity(product.commodity),
            dangerous,
        ]);
    };

    if (transportationMethod === 'sea') {
        const head = `${product.quantity} x ${product.type}`;

        if (product.type === 'LCL') return packageLines(' (sea)');
        if (product.type === 'By Volume or Weight (sea)') return groupageLine({ withDimensions: false });
        if (product.type === 'By Dimensions or Weight (sea)') return groupageLine({ withDimensions: true });

        if (product.type.indexOf('Flat') > -1) {
            return line(head, [dimensions(product.length, product.width, product.height, product.cmOrInches), grossWeight(), commodity(product.commodity), dangerous]);
        }
        if (product.type.indexOf('Open Top') > -1) {
            return line(head, [height(product.height, product.cmOrInches), grossWeight(), commodity(product.commodity), dangerous]);
        }
        if (product.type.indexOf('Reefer') > -1) {
            return line(head, [temperature(product.temperature, product.celsiusOrFahrenheit), grossWeight(), commodity(product.commodity), dangerous]);
        }
        return line(head, [commodity(product.commodity), grossWeight(), dangerous]);
    }

    if (transportationMethod === 'land') {
        const head = `${product.quantity} x ${product.type}`;

        // A land LCL (drayage) leg prices identically to ocean LCL.
        if (product.type === 'LCL') return packageLines('');

        if (product.type.includes('Reefer')) {
            return line(head, [temperature(product.temperature, product.celsiusOrFahrenheit), grossWeight(), commodity(product.commodity), dangerous]);
        }

        // Legacy land groupage types are priced by their dimensions, so keep them in the description.
        if (product.type === 'By Dimensions' || product.type === 'By Dimensions or Weight (land)') return groupageLine({ withDimensions: true });

        // Trucks and containers: no dimensions in the description.
        const weight = calcWeight({ product });
        const volume = calcTotalVolume({ product });
        return line(head, [amount('Total gross weight', weight?.val, weight?.postfix), amount('Total volume', volume?.val, volume?.postfix), commodity(product.commodity), dangerous]);
    }

    return product.type;
}

function checkProductErrors({ product }) {
    const er = [];

    if (!product.type) {
        er.push('type');
    } else if (product.type === 'Air Freight') {
        const airType = getAirType({ ...product });

        if (airType === 'dimensions') {
            if (isNaN(parseFloat(product.width)) || !product.width) er.push('width');
            if (isNaN(parseFloat(product.height)) || !product.height) er.push('height');
            if (isNaN(parseFloat(product.length)) || !product.length) er.push('length');
        }
        if (airType === 'volume') {
            if (isNaN(parseFloat(product.volume)) || !product.volume) er.push('volume');
        }
        if (airType === 'chargeable-weight') {
            if (isNaN(parseFloat(product.chargeableWeight)) || !product.chargeableWeight) er.push('chargeableWeight');
        }
        if (isNaN(parseFloat(product.weight)) || !product.weight) er.push('weight');
    } else if (product.type === 'LCL') {
        if (!product.calculations || product.calculations.length === 0) {
            return []; // Empty LCL is valid — it just won't be saved
        }
        const calcErrors = [];
        product.calculations.forEach((calc, i) => {
            if (calc.method === 'dimensions') {
                if (isNaN(parseFloat(calc.length))) calcErrors.push(`calc-${i}-length`);
                if (isNaN(parseFloat(calc.width))) calcErrors.push(`calc-${i}-width`);
                if (isNaN(parseFloat(calc.height))) calcErrors.push(`calc-${i}-height`);
                if (isNaN(parseFloat(calc.weight))) calcErrors.push(`calc-${i}-weight`);
            } else {
                if (isNaN(parseFloat(calc.volume))) calcErrors.push(`calc-${i}-volume`);
                if (isNaN(parseFloat(calc.weight))) calcErrors.push(`calc-${i}-weight`);
            }
            if (isNaN(parseInt(calc.quantity)) || parseInt(calc.quantity) < 1) calcErrors.push(`calc-${i}-quantity`);
        });
        return calcErrors;
    } else if (product.type === 'Air freight') {
        if (!product.calculations || product.calculations.length === 0) {
            return []; // Empty Air freight is valid, just not saved
        }
        const calcErrors = [];
        product.calculations.forEach((calc, i) => {
            if (calc.method === 'dimensions') {
                if (isNaN(parseFloat(calc.length))) calcErrors.push(`calc-${i}-length`);
                if (isNaN(parseFloat(calc.width))) calcErrors.push(`calc-${i}-width`);
                if (isNaN(parseFloat(calc.height))) calcErrors.push(`calc-${i}-height`);
                if (isNaN(parseFloat(calc.weight))) calcErrors.push(`calc-${i}-weight`);
            } else if (calc.method === 'volume') {
                if (isNaN(parseFloat(calc.volume))) calcErrors.push(`calc-${i}-volume`);
                if (isNaN(parseFloat(calc.weight))) calcErrors.push(`calc-${i}-weight`);
            } else if (calc.method === 'chargeable-weight') {
                if (isNaN(parseFloat(calc.chargeableWeight))) calcErrors.push(`calc-${i}-chargeableWeight`);
                if (isNaN(parseFloat(calc.weight))) calcErrors.push(`calc-${i}-weight`);
                // A package priced by chargeable weight is charged at the figure entered here and
                // nothing else - its gross weight is carried for the documents. That only holds
                // while the chargeable weight is the larger of the two, which is what it means.
                // Entered the other way round (a gross weight against a chargeable weight of zero,
                // say) the package silently charges less than it weighs. The rate search panel has
                // refused that pairing all along; the packages here never did.
                if (!isNaN(parseFloat(calc.chargeableWeight)) && !isNaN(parseFloat(calc.weight))
                    && parseFloat(calc.chargeableWeight) < parseFloat(calc.weight)) {
                    calcErrors.push(`calc-${i}-chargeable-weight-gross-weight`);
                }
            }
            if (isNaN(parseInt(calc.quantity)) || parseInt(calc.quantity) < 1) calcErrors.push(`calc-${i}-quantity`);
        });
        return calcErrors;
    } else {
        const required = (_.flatten(_.keys(PRODUCT_TYPES).map(tm => allProductTypes(tm))).find(p => p.name === product.type) || {}).required || [];
        const ask = (_.flatten(_.keys(PRODUCT_TYPES).map(tm => allProductTypes(tm))).find(p => p.name === product.type) || {}).ask || [];
        // Class, UN code and the MSDS file are optional on a dangerous product; a UN code that is
        // given must still be the 4-digit number.
        if (required.includes('dangerous')) {
            if (product.dangerous && product.dangerousUNCode && !String(product.dangerousUNCode).match(/^\d\d\d\d$/)) er.push('dangerousUNCode');
        }
        if (required.includes('quantity')) {
            if ((!parseInt(product.quantity)) || (parseInt(product.quantity) < 1)) er.push('quantity');
        }
        if (required.includes('dimensions')) {
            if (isNaN(parseFloat(product.width))) er.push('width');
            if (isNaN(parseFloat(product.height))) er.push('height');
            if (isNaN(parseFloat(product.length))) er.push('length');
        } else if (ask.includes('dimensions')) {
            if (!!product.width && isNaN(parseFloat(product.width))) er.push('width');
            if (!!product.height && isNaN(parseFloat(product.height))) er.push('height');
            if (!!product.length && isNaN(parseFloat(product.length))) er.push('length');
        }
        if (required.includes('weight')) {
            if (isNaN(parseFloat(product.weight))) er.push('weight');
        } else if (ask.includes('weight') && (!!product.weight)) {
            if (!!product.weight && isNaN(parseFloat(product.weight))) er.push('weight');
        }
        if (required.includes('height')) {
            if (isNaN(parseFloat(product.height))) er.push('height');
        } else if (ask.includes('height')) {
            if (!!product.height && isNaN(parseFloat(product.height))) er.push('height');
        }
        if (required.includes('temperature')) {
            if (isNaN(parseFloat(product.temperature))) er.push('temperature');
        } else if (ask.includes('temperature')) {
            if (!!product.temperature && isNaN(parseFloat(product.temperature))) er.push('temperature');
        }
        if (required.includes('chargeable-weight')) {
            // For Air Freight products, validate based on the selected input mode
            const airType = getAirType({
                width: product.width,
                height: product.height,
                length: product.length,
                volume: product.volume,
                chargeableWeight: product.chargeableWeight
            });

            if (airType === 'dimensions') {
                // By Dimensions: Length, Width, Height, and Weight are mandatory
                if (isNaN(parseFloat(product.length)) || !product.length) er.push('length');
                if (isNaN(parseFloat(product.width)) || !product.width) er.push('width');
                if (isNaN(parseFloat(product.height)) || !product.height) er.push('height');
                if (isNaN(parseFloat(product.weight)) || !product.weight) er.push('weight');
            } else if (airType === 'volume') {
                // By Volume: Volume and Weight are mandatory
                if (isNaN(parseFloat(product.volume)) || !product.volume) er.push('volume');
                if (isNaN(parseFloat(product.weight)) || !product.weight) er.push('weight');
            } else if (airType === 'chargeable-weight') {
                // By Chargeable Weight: Chargeable Weight and Weight are mandatory
                if (isNaN(parseFloat(product.chargeableWeight)) || !product.chargeableWeight) er.push('chargeableWeight');
                if (isNaN(parseFloat(product.weight)) || !product.weight) er.push('weight');
            }
        }
        if (required.includes('weight-or-measurement')) {
            if (isNaN(parseFloat(product.weight))) er.push('weight');
            if (isNaN(parseFloat(product.volume))) er.push('volume');
        } else if (ask.includes('weight-or-measurement')) {
            if (!!product.weightOrMeasurment && isNaN(parseFloat(product.weightOrMeasurment))) er.push('weightOrMeasurment');
        }
    }

    if (product.type === 'Air Freight' && product.chargeableWeight && product.weight && parseFloat(product.chargeableWeight) < parseFloat(product.weight)) {
        er.push('air-freight-chargeable-weight-gross-weight');
    }

    return er;
}

const createEmptyProduct = ({ transportationMethod, type } = {}) => {
    if (type === 'LCL') {
        return {
            id: uuidv4(),
            type: 'LCL',
            dangerous: false,
            kgOrLbs: 'kg',
            cmOrInches: 'cm',
            nonstackable: false,
            densityRatio: 1000,
            calculations: [],
        };
    }

    if (type === 'Air freight') {
        return {
            id: uuidv4(),
            type: 'Air freight',
            dangerous: false,
            kgOrLbs: 'kg',
            cmOrInches: 'cm',
            nonstackable: false,
            roundChargeableWeight: true,
            calculations: [],
        };
    }

    return {
        id: uuidv4(),
        type: transportationMethod === 'sea' ? '20\' Dry' : transportationMethod === 'air' ? 'Air freight' : transportationMethod === 'land' ? 'Dry' : null,
        kgOrLbs: 'kg',
        cmOrInches : 'cm',
        celsiusOrFahrenheit: 'c',
        width: '',
        height: '',
        length: '',
        weight: '',
        chargeableWeight: '',
        nonstackable: false,
        dangerous: false,
        temperature: '',
        quantity: 1,
        dangerousClass: '',
        dangerousUNCode: '',
        dangerousMSDS: '',
        commodity: '',
        comments: '',
    };
};

function createRateProduct({ productType, dangerous, transportationMethod, extraFilter }) {
    const products = createAllRateProducts({ transportationMethod });
    return products.find(p => p.type === productType && (p.dangerous || false) === (dangerous || false) && (extraFilter ? extraFilter(p) : true));
}

function createAllRateProducts({ transportationMethod }) {
    if (!transportationMethod) return [];

    const _p = _.flatten(_.compact(PRODUCT_TYPES[transportationMethod].map(p => {
        if (p.name === 'By Dimensions or Weight (sea)') return null;
        if (p.name === 'By Volume or Weight (sea)') return null;
        // Legacy land groupage types (see ProductTypes.js) are hidden from every other product
        // picker (no `group`) and kept only to validate existing data. This rate-product list
        // missed the same exclusion, so they were still selectable here despite having no default
        // dimensions/weight — making a rate created from them fail required-field validation on save.
        if (p.name === 'By Dimensions') return null;
        if (p.name === 'By Dimensions or Weight (land)') return null;
        if (p.name === 'LCL') {
            return [
                { id: uuidv4(), type: 'LCL', dangerous: false, quantity: 1, kgOrLbs: 'kg', cmOrInches: 'cm', calculations: [{ id: 'default', method: 'volume', volume: 1, weight: 1000, quantity: 1 }] },
                { id: uuidv4(), type: 'LCL', dangerous: true, quantity: 1, kgOrLbs: 'kg', cmOrInches: 'cm', calculations: [{ id: 'default', method: 'volume', volume: 1, weight: 1000, quantity: 1 }] },
            ];
        }
        if (p.name === 'Air Freight') return null;
        if (p.name === 'Air freight') {
            return [
                { id: uuidv4(), type: 'Air freight', dangerous: false, quantity: 1, kgOrLbs: 'kg', calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }] },
                { id: uuidv4(), type: 'Air freight', dangerous: true, quantity: 1, kgOrLbs: 'kg', calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }] },
            ];
        }


        return [{
            id: uuidv4(), 
            type: p.name, 
            dangerous: false, 
            quantity: 1,
            ...(p.name.includes('Flat') ? { cmOrInches: 'cm', length: 0, width: 0, height: 0 } : {}),
            ...(p.name.includes('Open Top') ? { cmOrInches: 'cm', height: 0 } : {}),
            ...(p.name.includes('Reefer') ? { celsiusOrFahrenheit: 'c', temperature: 0 } : {}),
            ...(p.name.includes('Air Freight') ? { chargeableWeight: 1, weight: 1, kgOrLbs: 'kg' } : {})
        }, {
            id: uuidv4(), 
            type: p.name, 
            dangerous: true, 
            quantity: 1,
            ...(p.name.includes('Flat') ? { cmOrInches: 'cm', length: 0, width: 0, height: 0 } : {}),
            ...(p.name.includes('Open Top') ? { cmOrInches: 'cm', height: 0 } : {}),
            ...(p.name.includes('Reefer') ? { celsiusOrFahrenheit: 'c', temperature: 0 } : {}),
            ...(p.name.includes('Air Freight') ? { chargeableWeight: 1, weight: 1, kgOrLbs: 'kg' } : {})
        }];
    })));

    return _p;
}


function splitProductsByType({ products, transportationMethod, useProductId, organization }) {
    const containers = _(products)
        .filter(s => s.type !== 'By Dimensions or Weight (sea)')
        .filter(s => s.type !== 'By Volume or Weight (sea)')
        // The land pair belongs here for the same reason as the sea pair above: loose cargo
        // is not a container. Left out, a land weight-or-measurement product was counted as a
        // container as well as by its weight or measurement, so it raised two charge lines and
        // its cost was charged on both of them.
        .filter(s => s.type !== 'By Dimensions or Weight (land)')
        .filter(s => s.type !== 'By Volume or Weight (land)')
        .filter(s => s.type !== 'LCL')
        .filter(s => s.type !== 'Air Freight')
        .filter(s => s.type !== 'Air freight')
        .filter(s => s.type !== 'By Dimensions')
        .value();

    const airFreight = _(products)
        .filter(p => p.type === 'Air Freight' || p.type === 'Air freight')
        .map(p => ({ product: p, weight: calcChargeableWeight({ product: p, transportationMethod, allProducts: products, round: organization.airRoundChargeableWeight })}))
        .filter(p => p.weight)
        .groupBy(p => useProductId ? p.product.id : p.weight.postfix)
        .value();

    const seaByWeightOrMeasurment = _(products)
        .filter(p => p.type === 'By Dimensions or Weight (sea)' || p.type === 'By Volume or Weight (sea)' || p.type === 'LCL')
        .map(p => ({ product: p, weight: calcWeightOrMeasurment({ product: p, transportationMethod, allProducts: products })}))
        .filter(p => p.weight)
        .groupBy(p => useProductId ? p.product.id : p.weight.postfix)
        .value();

    // LCL belongs here as well as in the sea bucket. It is a weight-or-measurement product
    // whichever way the cargo travels, and calcWeightOrMeasurment prices it without asking
    // about the mode. Left out, an LCL product on a land opportunity fell between the two
    // buckets - kept out of containers, and in a sea bucket the land charge lines never
    // read - so it got no charge line at all and any cost attached to it went uncounted.
    const landByWeightOrMeasurment = transportationMethod === 'land' ? _(products)
        .filter(p => p.type === 'By Dimensions or Weight (land)' || p.type === 'By Volume or Weight (land)' || p.type === 'By Dimensions' || p.type === 'LCL')
        .map(p => ({ product: p, weight: calcWeightOrMeasurment({ product: p, transportationMethod, allProducts: products })}))
        .filter(p => p.weight)
        .groupBy(p => useProductId ? p.product.id : p.weight.postfix)
        .value() : {};

    return { containers, airFreight, seaByWeightOrMeasurment, landByWeightOrMeasurment };
}

function getProductName({ product }) {
    if (product.type === 'Air Freight' || product.type === 'Air freight' ||
        product.type === 'By Dimensions or Weight (sea)' || product.type === 'By Volume or Weight (sea)' || product.type === 'LCL') {
        const qty = (product.type === 'Air freight' || product.type === 'LCL')
            ? _.sumBy(product.calculations || [], c => parseInt(c.quantity || 1))
            : product.quantity;
        return `${qty} package${qty > 1 ? 's' : ''}, Commodity: ${product.commodity || 'General'}`;
    }

    return product.type + (product.commodity ? ` (Commodity: ${product.commodity})` : '');
}

function findMatchingProducts({ product, products }) {
    return _.filter(products, p => {
        const _type = normalizeProductType(product.type);
        const _type2 = normalizeProductType(p.type);

        if (_type !== _type2) return false;
        if ((product.dangerous || false) !== (p.dangerous || false)) return false;
        // Skip commodity check for LCL — commodity is cosmetic on calculations
        if (_type !== 'LCL' && _type !== 'Air freight' && product.commodity && (product.commodity || '') !== (p.commodity || '')) return false;
        return true;
    });
}

function getAirType({ width, height, length, volume, chargeableWeight }) {
    if (!_.isNaN(parseInt(volume))) {
        return 'volume';
    }
    if (!_.isNaN(parseInt(width)) && !_.isNaN(parseInt(height)) && !_.isNaN(parseInt(length))) {
        return 'dimensions';
    }
    if (!_.isNaN(parseInt(chargeableWeight))) {
        return 'chargeable-weight';
    }
    return 'dimensions'; // Default
}

export function migrateToLCL(opportunity) {
    const products = opportunity.products || [];
    const wmTypes = ['By Volume or Weight (sea)', 'By Dimensions or Weight (sea)'];
    const wmProducts = products.filter(p => wmTypes.includes(p.type));

    if (wmProducts.length === 0) return opportunity;

    const firstWM = wmProducts[0];
    const dangerousProduct = wmProducts.find(p => p.dangerous);

    const lclProduct = {
        id: firstWM.id,
        type: 'LCL',
        dangerous: wmProducts.some(p => p.dangerous),
        ...(dangerousProduct ? {
            dangerousClass: dangerousProduct.dangerousClass,
            dangerousUNCode: dangerousProduct.dangerousUNCode,
            dangerousMSDS: dangerousProduct.dangerousMSDS,
        } : {}),
        kgOrLbs: firstWM.kgOrLbs || 'kg',
        cmOrInches: firstWM.cmOrInches || 'cm',
        nonstackable: wmProducts.some(p => p.nonstackable),
        calculations: wmProducts.map(p => {
            if (p.type === 'By Dimensions or Weight (sea)') {
                return {
                    id: uuidv4(),
                    method: 'dimensions',
                    length: p.length, width: p.width, height: p.height,
                    weight: p.weight, quantity: p.quantity || 1,
                    commodity: p.commodity || 'General',
                };
            } else {
                return {
                    id: uuidv4(),
                    method: 'volume',
                    volume: p.volume, weight: p.weight,
                    quantity: p.quantity || 1,
                    commodity: p.commodity || 'General',
                };
            }
        }),
    };

    const nonWM = products.filter(p => !wmTypes.includes(p.type));
    const newProducts = [...nonWM, lclProduct];

    // Migrate field values and buyingRates in offers
    const oldProductIds = wmProducts.map(p => p.id);
    const newProductId = lclProduct.id;
    const newOffers = (opportunity.offers || []).map(offer => ({
        ...offer,
        sections: (offer.sections || []).map(section => ({
            ...section,
            offers: (section.offers || []).map(o => ({
                ...o,
                fields: (o.fields || []).map(f => {
                    const newValues = {};
                    _.forEach(f.values, (val, key) => {
                        const newKey = oldProductIds.includes(key) ? newProductId : key;
                        if (!newValues[newKey]) newValues[newKey] = val;
                    });
                    const newBuyingRates = _.uniqBy(
                        (f.buyingRates || []).map(br => ({
                            ...br,
                            opportunityProductId: oldProductIds.includes(br.opportunityProductId) ? newProductId : br.opportunityProductId,
                        })),
                        br => `${br.rateId}|${br.fieldId}|${br.opportunityProductId}`
                    );
                    return { ...f, values: newValues, buyingRates: newBuyingRates };
                }),
            })),
        })),
    }));

    // Migrate buying rate products to LCL
    const newBuyingRates = (opportunity.buyingRates || []).map(br => migrateRateToLCL(br));

    return { ...opportunity, products: newProducts, offers: newOffers, buyingRates: newBuyingRates };
}

export function migrateRateToLCL(rate) {
    if (!rate?.product) return rate;
    const wmTypes = ['By Volume or Weight (sea)', 'By Dimensions or Weight (sea)'];
    if (!wmTypes.includes(rate.product.type)) return rate;
    return {
        ...rate,
        product: {
            id: rate.product.id,
            type: 'LCL',
            dangerous: rate.product.dangerous || false,
            quantity: 1,
            kgOrLbs: 'kg',
            cmOrInches: 'cm',
            calculations: [{ id: 'default', method: 'volume', volume: 1, weight: 1000, quantity: 1 }],
        },
    };
}

/**
 * After individual rate migration to LCL, detect paired rates (same route/dates/supplier,
 * one originally per-cbm and one per-ton) and calculate densityRatio from field values.
 * Merges the pair into a single LCL rate.
 */
export function migrateWMRatePairs(rates) {
    const wmTypes = ['By Volume or Weight (sea)', 'By Dimensions or Weight (sea)'];

    // Build a fingerprint for matching: source+destination+transshipment+dates+supplier+dangerous
    function rateFingerprint(r) {
        return [
            r.source?.id || r.source?.text || '',
            r.destination?.id || r.destination?.text || '',
            r.offer?.transshipment || '',
            r.offer?.validFrom || '',
            r.offer?.validUntil || '',
            r.supplier?.organization || '',
            r.supplier?.uniqueId || '',
            r.product?.dangerous ? 'dg' : 'ndg',
        ].join('|');
    }

    // Identify which migrated LCL rates were originally per-ton vs per-cbm
    // After migrateRateToLCL, the original product info is lost. We need to check
    // the default calculation: { volume: 1, weight: 1000 } means it was migrated
    // but we can't tell cbm vs ton from that alone.
    // Instead, look at unmigrated rates or check if there's a pair with matching fingerprint.

    // Group LCL rates by fingerprint
    const lclRates = rates.filter(r => r.product?.type === 'LCL');
    const otherRates = rates.filter(r => r.product?.type !== 'LCL');

    const groups = _.groupBy(lclRates, rateFingerprint);
    const processedIds = new Set();
    const result = [...otherRates];

    for (const [fp, group] of Object.entries(groups)) {
        if (group.length < 2) {
            result.push(...group);
            continue;
        }

        // Look for pairs to merge
        let paired = false;
        for (let i = 0; i < group.length && !paired; i++) {
            for (let j = i + 1; j < group.length && !paired; j++) {
                const rateA = group[i];
                const rateB = group[j];
                if (processedIds.has(rateA.id) || processedIds.has(rateB.id)) continue;

                const allFieldsA = _.flatten((rateA.offer?.sections || []).map(s => s.offers?.[0]?.fields || []));
                const allFieldsB = _.flatten((rateB.offer?.sections || []).map(s => s.offers?.[0]?.fields || []));

                // Must have at least one matching non-flat field title
                const nonFlatA = allFieldsA.filter(f => f.type !== 'flat' && f.type !== 'string');
                const nonFlatB = allFieldsB.filter(f => f.type !== 'flat' && f.type !== 'string');
                const hasMatchingField = nonFlatA.some(fA => nonFlatB.some(fB => fA.title === fB.title));
                if (!hasMatchingField) continue;

                // Only legacy pairs are merged: two rates that each price the charge as a plain amount
                // (one per cbm, one per ton). A charge that already has a formula is a complete rate on
                // its own (e.g. an LCL W/M formula from a tariff upload), so two of them on the same
                // route are two rates, not halves of one. Merging them read their empty values and
                // showed max(0 * cbm, 0 * tons) — NZ$0 on the Rates page and in rate search.
                const usesFormula = f => Object.values(f.values || {}).some(v => v?.formula);
                const matchingA = nonFlatA.filter(fA => nonFlatB.some(fB => fB.title === fA.title));
                const matchingB = nonFlatB.filter(fB => nonFlatA.some(fA => fA.title === fB.title));
                if (matchingA.some(usesFormula) || matchingB.some(usesFormula)) continue;

                // Determine density ratio from the first field pair with differing values
                let densityRatio = 1000;
                for (const fA of nonFlatA) {
                    const fB = nonFlatB.find(f => f.title === fA.title);
                    if (!fB) continue;
                    const valA = parseFloat(Object.values(fA.values || {})[0]?.value) || 0;
                    const valB = parseFloat(Object.values(fB.values || {})[0]?.value) || 0;
                    if (valA > 0 && valB > 0 && valA !== valB) {
                        const [perCbmVal, perTonVal] = valA > valB ? [valA, valB] : [valB, valA];
                        densityRatio = Math.round((perCbmVal * 1000) / perTonVal);
                        break;
                    }
                }

                console.log(`[migrateWMRatePairs] Merging rates: supplier: ${rateA.supplier?.organization}, route: ${rateA.source?.text} → ${rateA.destination?.text}, densityRatio: ${densityRatio}`);

                // Merge: use rateA as base, process all fields
                const mergedSections = _.cloneDeep(rateA.offer?.sections || []);
                const mergedFieldTitles = new Set();

                // Process each section's fields
                for (const section of mergedSections) {
                    for (const offer of (section.offers || [])) {
                        offer.fields = (offer.fields || []).map(fA => {
                            mergedFieldTitles.add(fA.title);
                            if (fA.type === 'flat' || fA.type === 'string') return fA;

                            const fB = allFieldsB.find(f => f.title === fA.title && f.type !== 'flat' && f.type !== 'string');
                            if (!fB) return fA; // No match in B, keep as-is

                            const valKeyA = Object.keys(fA.values || {})[0];
                            const valKeyB = Object.keys(fB.values || {})[0];
                            const vA = fA.values?.[valKeyA];
                            const vB = fB.values?.[valKeyB];

                            // Same value and same formula → keep as-is
                            if (vA?.value === vB?.value && vA?.formula === vB?.formula) {
                                console.log(`[migrateWMRatePairs]   "${fA.title}": same value (${vA?.value}), keeping as-is`);
                                return fA;
                            }

                            // Different values → create LCL W/M formula
                            const aVal = parseFloat(vA?.value) || 0;
                            const bVal = parseFloat(vB?.value) || 0;
                            const [perCbmVal, perTonVal] = aVal > bVal ? [aVal, bVal] : [bVal, aVal];

                            const wmFormula = formulaFromLCLWM({ perTon: perTonVal, perCbm: perCbmVal, densityRatio });
                            console.log(`[migrateWMRatePairs]   "${fA.title}": values differ (${aVal} vs ${bVal}), formula: ${wmFormula}`);

                            return {
                                ...fA,
                                values: {
                                    [valKeyA]: {
                                        currency: vA?.currency || vB?.currency,
                                        formula: wmFormula,
                                        value: 0,
                                    }
                                }
                            };
                        });
                    }
                }

                // Add fields from B that don't exist in A
                for (const fB of allFieldsB) {
                    if (!mergedFieldTitles.has(fB.title)) {
                        if (mergedSections.length > 0 && mergedSections[0].offers?.[0]) {
                            mergedSections[0].offers[0].fields.push(fB);
                        }
                    }
                }

                result.push({
                    ...rateA,
                    offer: { ...rateA.offer, sections: mergedSections },
                    product: { ...rateA.product, densityRatio },
                });
                processedIds.add(rateA.id);
                processedIds.add(rateB.id);
                paired = true;
            }
        }

        // Add unpaired rates from this group
        for (const r of group) {
            if (!processedIds.has(r.id)) {
                result.push(r);
            }
        }
    }

    const pairedCount = processedIds.size / 2;
    if (pairedCount > 0) {
        console.log(`[migrateWMRatePairs] Merged ${pairedCount} rate pairs, ${result.length} rates total (was ${rates.length})`);
    }

    return result;
}

function getProductShortDescription({ product }) {
    if (product.type === 'LCL') {
        const totalQty = _.sumBy(product.calculations || [], c => parseInt(c.quantity || 1));
        return `LCL (${totalQty} package${totalQty !== 1 ? 's' : ''})`;
    }
    if (product.type === 'Air freight') {
        const totalQty = _.sumBy(product.calculations || [], c => parseInt(c.quantity || 1));
        return `Air freight (${totalQty} package${totalQty !== 1 ? 's' : ''})`;
    }
    return `${product.quantity || 1} x ${product.type}`;
}

export function migrateRateToAirFreight(rate) {
    if (!rate?.product) return rate;
    if (rate.product.type !== 'Air Freight') {
        // Still remap legacy air per-unit-type field types on already-migrated rates
        if (rate.product.type === 'Air freight') return remapAirFieldTypesInRate(rate);
        return rate;
    }
    return remapAirFieldTypesInRate({
        ...rate,
        product: {
            id: rate.product.id,
            type: 'Air freight',
            dangerous: rate.product.dangerous || false,
            quantity: 1,
            kgOrLbs: rate.product.kgOrLbs || 'kg',
            calculations: [{ id: 'default', method: 'chargeable-weight', chargeableWeight: 1, weight: 1, quantity: 1 }],
        },
    });
}

// Air: the old "per-unit-type" / "per-unit-type-weight" field types (one per product)
// are no longer selectable since Air freight is now a single unified product.
// They are semantically equivalent to "per-unit" / "per-unit-weight" (one aggregated CW/GW).
const AIR_FIELD_TYPE_REMAP = {
    'per-unit-type': 'per-unit',
    'per-unit-type-weight': 'per-unit-weight',
};

// Remap legacy air per-unit-type field types in an org's forms so new rates
// created from these templates use the current field types.
export function migrateOrgForms(org) {
    if (!org?.forms) return org;
    const newForms = _.mapValues(org.forms, form => {
        const tms = form?.transportationMethods || [];
        if (!tms.includes('air')) return form;
        const sections = (form.sections || []).map(section => ({
            ...section,
            fields: (section.fields || []).map(f => {
                const newType = AIR_FIELD_TYPE_REMAP[f.type];
                return newType ? { ...f, type: newType } : f;
            }),
        }));
        return { ...form, sections };
    });
    return { ...org, forms: newForms };
}
function remapAirFieldTypes(offers) {
    return (offers || []).map(offer => ({
        ...offer,
        sections: (offer.sections || []).map(section => ({
            ...section,
            offers: (section.offers || []).map(o => ({
                ...o,
                fields: (o.fields || []).map(f => {
                    const newType = AIR_FIELD_TYPE_REMAP[f.type];
                    return newType ? { ...f, type: newType } : f;
                }),
            })),
        })),
    }));
}
function remapAirFieldTypesInRate(rate) {
    if (!rate?.offer?.sections) return rate;
    return {
        ...rate,
        offer: {
            ...rate.offer,
            sections: rate.offer.sections.map(section => ({
                ...section,
                offers: (section.offers || []).map(o => ({
                    ...o,
                    fields: (o.fields || []).map(f => {
                        const newType = AIR_FIELD_TYPE_REMAP[f.type];
                        return newType ? { ...f, type: newType } : f;
                    }),
                })),
            })),
        },
    };
}

export function migrateToAirFreight(opportunity) {
    const products = opportunity.products || [];
    const isAirType = (t) => t === 'Air Freight' || t === 'Air freight';
    const airProducts = products.filter(p => isAirType(p.type));
    const hasLegacy = airProducts.some(p => p.type === 'Air Freight');
    // Consolidate if any legacy product exists, or if multiple unified Air freight products exist
    // (unified model requires exactly one Air freight product per opportunity).
    const needsConsolidation = hasLegacy || airProducts.length > 1;
    // For air opportunities, also remap legacy field types even when no consolidation is needed
    const isAir = opportunity.transportationMethod === 'air';
    if (!needsConsolidation) {
        if (!isAir) return opportunity;
        // Also repair orphaned opportunityProductId references: in air opportunities, there is
        // one unified "Air freight" product. Any field.buyingRates[].opportunityProductId or
        // field.values[key] that points at a product id no longer in products[] should be remapped
        // to the current Air freight product id.
        const unifiedAir = products.find(p => p.type === 'Air freight');
        const validProductIds = new Set(products.map(p => p.id));
        const remapOrphanProductId = (id) => {
            if (!id || validProductIds.has(id) || !unifiedAir) return id;
            return unifiedAir.id;
        };
        const newOffers = remapAirFieldTypes(opportunity.offers).map(offer => ({
            ...offer,
            sections: (offer.sections || []).map(section => ({
                ...section,
                offers: (section.offers || []).map(o => ({
                    ...o,
                    fields: (o.fields || []).map(f => {
                        if (!unifiedAir) return f;
                        const newValues = {};
                        _.forEach(f.values, (val, key) => {
                            const newKey = (key === 'flat' || key === 'string' || key === 'custom') ? key : remapOrphanProductId(key);
                            if (!newValues[newKey]) newValues[newKey] = val;
                        });
                        const newBuyingRatesLinks = _.uniqBy(
                            (f.buyingRates || []).map(br => ({
                                ...br,
                                opportunityProductId: remapOrphanProductId(br.opportunityProductId),
                            })),
                            br => `${br.rateId}|${br.fieldId}|${br.opportunityProductId}`
                        );
                        return { ...f, values: newValues, buyingRates: newBuyingRatesLinks };
                    }),
                })),
            })),
        }));
        const newBuyingRates = (opportunity.buyingRates || []).map(br => remapAirFieldTypesInRate(br));
        return { ...opportunity, offers: newOffers, buyingRates: newBuyingRates };
    }

    const firstAir = airProducts[0];
    const dangerousProduct = airProducts.find(p => p.dangerous);

    const airFreightProduct = {
        id: firstAir.id,
        type: 'Air freight',
        dangerous: airProducts.some(p => p.dangerous),
        ...(dangerousProduct ? {
            dangerousClass: dangerousProduct.dangerousClass,
            dangerousUNCode: dangerousProduct.dangerousUNCode,
            dangerousMSDS: dangerousProduct.dangerousMSDS,
        } : {}),
        kgOrLbs: firstAir.kgOrLbs || 'kg',
        cmOrInches: firstAir.cmOrInches || 'cm',
        nonstackable: airProducts.some(p => p.nonstackable),
        roundChargeableWeight: airProducts.some(p => p.roundChargeableWeight) || false,
        calculations: _.flatten(airProducts.map(p => {
            // Already-unified product: keep its existing calculations
            if (p.type === 'Air freight' && Array.isArray(p.calculations)) {
                return p.calculations;
            }
            // Legacy 'Air Freight': derive a single calculation from product fields
            const hasVolume = p.volume && !p.length;
            const hasDimensions = p.length && p.width && p.height;
            if (hasDimensions) {
                return [{ id: uuidv4(), method: 'dimensions', length: p.length, width: p.width, height: p.height, weight: p.weight, quantity: p.quantity || 1, commodity: p.commodity || 'General' }];
            } else if (hasVolume) {
                return [{ id: uuidv4(), method: 'volume', volume: p.volume, weight: p.weight, quantity: p.quantity || 1, commodity: p.commodity || 'General' }];
            } else {
                return [{ id: uuidv4(), method: 'chargeable-weight', chargeableWeight: p.chargeableWeight || 0, weight: p.weight, quantity: p.quantity || 1, commodity: p.commodity || 'General' }];
            }
        })),
    };

    const nonAir = products.filter(p => !isAirType(p.type));
    const newProducts = [...nonAir, airFreightProduct];

    // Migrate field values and buyingRates (same pattern as migrateToLCL).
    // For air opportunities there is now a single unified product. Remap any key/link pointing
    // at an id that isn't a current opportunity product (including orphaned ids from legacy data
    // where rates were mis-linked via rate.product.id instead of an opportunity product id).
    const oldProductIds = airProducts.map(p => p.id);
    const newProductId = airFreightProduct.id;
    const validProductIds = new Set(newProducts.map(p => p.id));
    const remapProductId = (id) => {
        if (!id) return id;
        if (oldProductIds.includes(id)) return newProductId;
        if (validProductIds.has(id)) return id;
        // Orphan id (not one of the old air products and not a current product) → remap to unified Air freight.
        return newProductId;
    };
    const newOffers = (opportunity.offers || []).map(offer => ({
        ...offer,
        sections: (offer.sections || []).map(section => ({
            ...section,
            offers: (section.offers || []).map(o => ({
                ...o,
                fields: (o.fields || []).map(f => {
                    const newValues = {};
                    _.forEach(f.values, (val, key) => {
                        const newKey = (key === 'flat' || key === 'string' || key === 'custom') ? key : remapProductId(key);
                        if (!newValues[newKey]) newValues[newKey] = val;
                    });
                    const newBuyingRates = _.uniqBy(
                        (f.buyingRates || []).map(br => ({
                            ...br,
                            opportunityProductId: remapProductId(br.opportunityProductId),
                        })),
                        br => `${br.rateId}|${br.fieldId}|${br.opportunityProductId}`
                    );
                    const remappedType = AIR_FIELD_TYPE_REMAP[f.type] || f.type;
                    return { ...f, type: remappedType, values: newValues, buyingRates: newBuyingRates };
                }),
            })),
        })),
    }));

    // Also migrate buying rate products and their field types
    const newBuyingRates = (opportunity.buyingRates || []).map(br => remapAirFieldTypesInRate(migrateRateToAirFreight(br)));

    return { ...opportunity, products: newProducts, offers: newOffers, buyingRates: newBuyingRates };
}

export { checkProductErrors, createEmptyProduct, splitProductsByType, getProductName, findMatchingProducts, getProductDescription, getProductShortDescription, createRateProduct, createAllRateProducts }
